import { find, filter, first, some } from 'lodash';
import { IPatchOperation, PatchOpType } from "../modelFactory";
import siteVisitFactory, { ISiteVisit, SiteVisitType } from "./siteVisitModel";
import { Iterable } from "immutable";
import jobCostFactory, { IJobCost } from "./jobCostModel";
import { ISiteVisitJobCost } from './siteVisitJobCostModel';
import jobFactory, { IJob } from './jobModel';
import { IChangeOrder } from './changeOrderModel';
import { IInstallSheet } from './installSheetModel';

export interface IContractSelection { 
	selected: boolean; 
	disabled: boolean; 
	contract: IJobCost;
	changeOrder?: IChangeOrder;
	installSheet?: IInstallSheet;
}

class OperationsModelFacade {
	private _job: IJob;
	private _jobCosts: IJobCost[];
	private _siteVisits: ISiteVisit[];

	constructor(job: IJob, jobCosts: IJobCost[], visits: ISiteVisit[]) {
		this._job = job;
		this._jobCosts = jobCosts;
		this._siteVisits = visits;
	}

	addVisit(type: SiteVisitType, selections: IContractSelection[]) {
		const selected = selections.filter(s => s.selected).map(s => ( { contract: s.contract, changeOrder: s.changeOrder, installSheet: s.installSheet} ));
		if(selected.length) {
			this._siteVisits.push(siteVisitFactory({ type, jobKey: selected[0].contract.jobKey, jobCosts: selected.map(c => { return { jobCostKey: c.contract.key, changeOrderKey: c.changeOrder?.key, installSheetKey: c.installSheet?.key } })}));
		}
	}

	removeVisit(key: string) {
		const idx = this._siteVisits.findIndex(v => v.key === key);

		if(idx !== -1) {
			this._siteVisits.splice(idx, 1);
		}
	}

	updateVisitSelections(siteVisitKey: string, selections: IContractSelection[]) {
		const visit = this._siteVisits.find(v => v.key === siteVisitKey);
		const needsInstallSheet = [SiteVisitType.inspection, SiteVisitType.install].indexOf(visit.type) !== -1;
		const toRemove = [];

		// removed
		for(const visitContract of visit.jobCosts.toArray()) {
			let possibleMatches: IContractSelection[] = filter(selections, (s: IContractSelection) => s.contract.key === visitContract.jobCostKey)

			if(visitContract.changeOrderKey) {
				possibleMatches = filter(possibleMatches, (s: IContractSelection) => s.changeOrder?.key === visitContract.changeOrderKey);
			}

			const matchingSelection:IContractSelection = needsInstallSheet? find(possibleMatches, m => m.installSheet?.key === visitContract.installSheetKey): first(possibleMatches);

			if(!matchingSelection?.selected) {
				toRemove.push(visitContract);
			}
		}

		// added
		for(const selection of selections.filter(s => s.selected)) {
			let possibleMatches:ISiteVisitJobCost[] = filter(visit.jobCosts, (jc:ISiteVisitJobCost) => jc.jobCostKey === selection.contract.key);

			if(selection.changeOrder) {
				possibleMatches = filter(possibleMatches, (jc: ISiteVisitJobCost) => jc.changeOrderKey === selection.changeOrder.key);
			}

			if(selection.installSheet) {
				possibleMatches = filter(possibleMatches, (jc: ISiteVisitJobCost) => jc.installSheetKey === selection.installSheet.key)
			}

			if(possibleMatches.length === 0) {
				visit.jobCosts.add({ jobCostKey: selection.contract.key, changeOrderKey: selection.changeOrder?.key, installSheetKey: selection.installSheet?.key });
			}
		}

		for(const removed of toRemove) {
			visit.jobCosts.delete(removed.key);
		}
	}

	get job() {
		return this._job;
	}

	get jobCosts() {
		return this._jobCosts;
	}

	get visits() {
		return this._siteVisits;
	}

	get deliveries() {
		return this._siteVisits.filter(v => v.type === SiteVisitType.delivery);
	}

	get installations() {
		return this._siteVisits.filter(v => v.type === SiteVisitType.install);
	}

	needsRebase(storeJob: IJob, storeJobCosts: Iterable<string, IJobCost>, storeVisits: Iterable<string, ISiteVisit>) {
		if(this._job.key !== storeJob.key || this._job.itemVersion !== storeJob.itemVersion) {
			return true;
		}
		
		if(storeJobCosts.some(jc => !this._jobCosts.find(jc2 => jc2.key === jc.key && jc2.itemVersion === jc.itemVersion))) {
			return true;
		}
		// status log check is necessary because the dashboard list shape does not include the status log
		return storeVisits.some(v => !this._siteVisits.find(sv => sv.key === v.key && sv.itemVersion === v.itemVersion && sv.jobCosts.length() === v.jobCosts.length() && sv.statusLog?.length() === v.statusLog?.length()));
	}

	getPatch(target: OperationsModelFacade) {
		const patch:IPatchOperation[] = [];
		const jobPatch = this.job.getPatch(target.job);

		// TODO: - coordinate patches between issues and site visits
		// if an issue is being added that needs a site visit - add the site visit before the issue
		// if an issue is being removed and leaving a visit orphaned, delete the visit

		if(jobPatch.length) {
			patch.push({ op: PatchOpType.replace, path: `job/${this.job.key}`, value: jobPatch });
		}

		for(const jobCost of this._jobCosts) {
			const targetJc = target._jobCosts.find(jc => jc.key === jobCost.key);
			
			if(targetJc) {
				const targetPatch = jobCost.getPatch(targetJc);
				if(targetPatch.length) {
					patch.push({ op: PatchOpType.replace, path: `jobCost/${jobCost.key}`, value: targetPatch });
				}
			}
		}

		for(const visit of this._siteVisits) {
			const targetVisit = target._siteVisits.find(v => v.key === visit.key);

			if(targetVisit) {
				const targetPatch = visit.getPatch(targetVisit);
				if(targetPatch.length) {
					patch.push({ op: PatchOpType.replace, path: `visit/${visit.key}`, value: targetPatch});
				}
			}
			else {
				patch.push({ op: PatchOpType.add, path: 'visit', value: visit.toDocument() })
			}
		}

		for(const targetVisit of target._siteVisits) {
			const visit = this._siteVisits.find(v => v.key === targetVisit.key);

			if(!visit) {
				patch.push({ op: PatchOpType.remove, path: `visit/${targetVisit.key}`});
			}
		}

		return patch;
	}
	toDocument () {
		const result: Record<string, any> = {job: this._job?.toDocument(), jobCosts: [], visits: [] }

		for(const jobCost of this._jobCosts) {
			result.jobCosts.push(jobCost.toDocument());
		}

		for(const visit of this._siteVisits) {
			result.visits.push(visit.toDocument());
		}

		return result;
	}
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export type IOperationsModelFacade = Omit<OperationsModelFacade, "constructor">
export function operationsModelFacadeFactory(doc?: { job: IJob, jobCosts: IJobCost[], visits: ISiteVisit[] }): IOperationsModelFacade {
	const job = doc?.job? jobFactory(doc.job): null;
	let jobCosts = [];
	let visits = [];
	
	if(doc?.visits) {
		visits = doc.visits.map(siteVisitFactory)
	}

	if(doc?.jobCosts) {
		jobCosts = doc.jobCosts.map(jobCostFactory)
	}

	return new OperationsModelFacade(job, jobCosts, visits);
}